import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ScreenContainerRef } from '../../../../context'
import { isListViewTypeBoard } from '../../../../utils/listViewType'
import { FilterDivider } from '../../../FilterDivider'
import { PageActions } from '../../../Page'
import { Pagination } from '../../../Pagination'
import { SelectionToolbar } from '../../../SelectionToolbar'
import { Skeleton } from '../../../Skeleton'
import {
  getSelectedSize,
  getTableResetSortingButton,
  Table,
  TableConfigModal,
  TableControlBar,
  TableControlButtons,
  TableMassAction,
} from '../../../Table'
import {
  getRentalPortalBoardNextSelection,
  getRentalPortalBoardSelectionState,
} from '../../RentalPortal.utils'
import { RentalPortalViewRegimeControl } from '../../RentalPortalViewRegimeControl'
import { RentalPortalApplicantBoard } from '../RentalPortalApplicantBoard'
import {
  APPLICANTS_TABLE_SKELETON_ROWS,
  QA_NAME_APPLICANTS_TABLE,
} from '../RentalPortalApplicants.constants'
import { useRentalPortalApplicantsMassActions } from '../RentalPortalApplicants.hooks'
import { getMockApplicantsDefaultColumns } from '../RentalPortalApplicants.mocks'
import {
  getIsApplicantActionArchive,
  getIsApplicantArchivable,
} from '../RentalPortalApplicants.utils'
import { RentalPortalApplicantsTableRow } from '../RentalPortalApplicantsTableRow'

import * as styles from './RentalPortalApplicantsTableLayout.module.css'

function RentalPortalApplicantsTableLayout({
  activeItem,
  arrowNavigationConfig,
  changeViewTypeButtonProps,
  emptyState,
  errorState,
  getApplicantItemActions,
  hasBodyError,
  hasHandoff,
  hasTable,
  listViewType,
  tablePage,
  onCallRowAction,
  onChangeFocusedRow,
  onClickRow,
  onMouseDownRow,
  onShowMore,
}) {
  const { t } = useTranslation()

  const [massActionModal, setMassActionModal] = useState(null)

  const {
    allItems,
    checkIsItemSelected,
    columns,
    entity,
    exclude,
    excludeIds,
    filterValues,
    handleApply,
    handleCleanSelection,
    handleConfigOpen,
    handleExclude,
    handleInitializeSelection,
    handleRefresh,
    handleResetOrdering,
    handleSelect,
    handleSelectAll,
    handleSelectAllPages,
    handleSetColumns,
    handleSetOrdering,
    handleSetPagination,
    handleSetSize,
    handleUnselectAllPages,
    isAllSelected,
    isConfigOpen,
    isLoading,
    isRefetching,
    isRemoveSelection,
    items,
    ordering,
    pageScrollWrapperRef,
    pagination,
    requiredColumns,
    selectAllPages,
    selected,
    setConfigOpen,
    stageTotals,
  } = tablePage

  const isBoardRegime = isListViewTypeBoard(listViewType)

  useEffect(() => {
    handleCleanSelection()
  }, [isBoardRegime])

  function handleCloseMassActionModal() {
    setMassActionModal(null)
  }

  const { handleArchiveApplicant, massActions, setMassAction } =
    useRentalPortalApplicantsMassActions({
      allItems,
      excludeIds,
      filterValues,
      selectAllPages,
      selected,
      setModal: setMassActionModal,
      onCleanSelection: handleCleanSelection,
      onClose: handleCloseMassActionModal,
      onReload: handleRefresh,
    })

  function handleCallRowAction(action, item) {
    if (getIsApplicantActionArchive(action)) {
      handleArchiveApplicant(item)
    } else {
      onCallRowAction(action, item)
    }
  }

  const selectedIds = selected.map(applicant => applicant.id)

  const regimeSelection = getRentalPortalBoardSelectionState({
    getIsSelectable: getIsApplicantArchivable,
    items: isBoardRegime ? allItems : items.items,
    selectedIds,
  })

  const hasMassActions = massActions.length > 0

  const hasSelectionToolbar =
    !isBoardRegime && (selected.length > 0 || selectAllPages)

  const hasBoardSelectionCount = isBoardRegime && selected.length > 0

  const isTableEmpty = !(items?.items?.length > 0) && !isLoading

  function handleSelectGroup(groupItems, isChecked) {
    handleInitializeSelection(
      getRentalPortalBoardNextSelection({
        isChecked,
        items: groupItems,
        selected,
      })
    )
  }

  function handleSelectAllInRegime() {
    handleSelectGroup(
      regimeSelection.selectableItems,
      !regimeSelection.isAllSelected && !regimeSelection.isPartlySelected
    )
  }

  return (
    <>
      {hasTable ? (
        <>
          <TableControlBar>
            {hasMassActions ? (
              <TableMassAction
                responsive
                actions={massActions}
                disabled={
                  !selectAllPages && regimeSelection.selectableCount === 0
                }
                isAllSelected={
                  selectAllPages ? isAllSelected : regimeSelection.isAllSelected
                }
                isLoading={isLoading}
                isRemoveSelection={
                  selectAllPages
                    ? isRemoveSelection
                    : regimeSelection.isPartlySelected
                }
                selectedSize={getSelectedSize({
                  exclude,
                  pagination,
                  selectAllPages,
                  selected,
                })}
                setMassAction={setMassAction}
                onSelectAll={
                  selectAllPages ? handleSelectAll : handleSelectAllInRegime
                }
              />
            ) : null}
            <PageActions className={styles.pageActions}>
              <TableControlButtons
                keepChangeViewTypeButtonOnNarrowScreen
                ChangeViewTypeButton={RentalPortalViewRegimeControl}
                DensityButton={null}
                ResetSortingButton={getTableResetSortingButton(ordering)}
                changeViewTypeButtonProps={changeViewTypeButtonProps}
                className={styles.controlButtons}
                configButtonProps={{
                  disabled: isBoardRegime,
                  onClick: handleConfigOpen,
                }}
                isSkeleton={isLoading}
                refreshButtonProps={{
                  isLoading: isRefetching,
                  onClick: handleRefresh,
                }}
                resetSortingProps={{ onClick: handleResetOrdering }}
              />
              <FilterDivider />
              {isLoading ? (
                <Skeleton className={styles.paginationSkeleton} />
              ) : (
                <Pagination
                  responsive
                  disabled={isBoardRegime}
                  value={pagination}
                  onChange={handleSetPagination}
                  onChangeSize={handleSetSize}
                />
              )}
            </PageActions>
          </TableControlBar>
          {hasBodyError ? (
            errorState
          ) : isBoardRegime ? (
            <RentalPortalApplicantBoard
              activeItem={activeItem}
              emptyState={emptyState}
              getActions={getApplicantItemActions}
              hasHandoff={hasHandoff}
              hasSelection={hasMassActions}
              isLoading={isLoading}
              items={allItems}
              selected={selected}
              selectNotAllowedTooltip={t(
                'RentalPortal.ApplicantArchiveNotAllowedTooltipKey'
              )}
              stageTotals={stageTotals}
              onCallAction={handleCallRowAction}
              onClickCard={onClickRow}
              onSelectCard={handleSelect}
              onSelectColumn={handleSelectGroup}
              onShowMore={onShowMore}
            />
          ) : isTableEmpty ? (
            emptyState
          ) : (
            <Table
              TableRow={RentalPortalApplicantsTableRow}
              arrowNavigationConfig={arrowNavigationConfig}
              checkIsItemSelected={checkIsItemSelected}
              columns={columns}
              containerRef={pageScrollWrapperRef}
              context={QA_NAME_APPLICANTS_TABLE}
              headerRowProps={{
                ordering,
                onApply: handleApply,
                onSetColumns: handleSetColumns,
                onSetOrdering: handleSetOrdering,
              }}
              isLoading={isLoading}
              items={items?.items}
              rowProps={{
                activeItem,
                exclude,
                getActions: getApplicantItemActions,
                getIsItemSelectable: getIsApplicantArchivable,
                hideCheckbox: !hasMassActions,
                isSelectedAll: selectAllPages,
                selected,
                selectNotAllowedTooltip: t(
                  'RentalPortal.ApplicantArchiveNotAllowedTooltipKey'
                ),
                onCallAction: handleCallRowAction,
                onClick: onClickRow,
                onMouseDown: onMouseDownRow,
                onSelect: selectAllPages ? handleExclude : handleSelect,
              }}
              skeletonRows={APPLICANTS_TABLE_SKELETON_ROWS}
              onChangeFocusedRow={onChangeFocusedRow}
            />
          )}
        </>
      ) : (
        emptyState
      )}
      {hasSelectionToolbar ? (
        <SelectionToolbar
          allSelected={selectAllPages || selected.length === pagination.count}
          count={pagination.count}
          excludeCount={exclude.length}
          selected={selected.length}
          onClose={handleUnselectAllPages}
          onSelectAll={handleSelectAllPages}
        />
      ) : hasBoardSelectionCount ? (
        <SelectionToolbar
          selected={selected.length}
          onClose={handleCleanSelection}
        />
      ) : null}
      {isConfigOpen && !isBoardRegime ? (
        <TableConfigModal
          columns={columns}
          entity={entity}
          getDefaultColumns={getMockApplicantsDefaultColumns}
          requiredColumns={requiredColumns}
          setColumns={handleSetColumns}
          setConfigOpen={setConfigOpen}
          onApply={handleApply}
        />
      ) : null}
      <ScreenContainerRef value={null}>{massActionModal}</ScreenContainerRef>
    </>
  )
}

export default RentalPortalApplicantsTableLayout
